import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import { UploadExceptionsWizardContextSteps } from "@app/exceptions/contexts/interfaces";
import { UploadExceptionsContext, UploadExceptionsProvider } from "@app/exceptions/contexts/UploadExceptionsContext";
import ExceptionsFiltersContextProvider from "@app/exceptions/contexts/ExceptionsFiltersContext";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { toast } from "react-toastify";
import UploadExceptionsImport from "@app/exceptions/steps/UploadExceptionsImport";
import UploadExceptionsReview from "@app/exceptions/steps/UploadExceptionsReview";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";

const UploadExceptionsWizard: FC = () => {
  return (
    <ExceptionsFiltersContextProvider>
      <UploadExceptionsProvider>
        <UploadExceptionsWizardContent />
      </UploadExceptionsProvider>
    </ExceptionsFiltersContextProvider>
  );
}

const UploadExceptionsWizardContent = (): JSX.Element => {
  const wizardContext = useContext(UploadExceptionsContext);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const exceptionsService = useRef(new ExceptionsService()).current;

  const [pageIsValid, setPageIsValid] = useState(false);
  const [redirectToView, setRedirectToView] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const humanReadablePageNames = {
    [UploadExceptionsWizardContextSteps.Upload]: "Upload List",
    [UploadExceptionsWizardContextSteps.Review]: "Review",
  };

  const getCurrentStep = () => {
    return wizardContext.page;
  }

  const handleUpdatePage = (newPage: number) => {
    const step = Object.entries(UploadExceptionsWizardContextSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as UploadExceptionsWizardContextSteps;
    wizardContext.setPage(UploadExceptionsWizardContextSteps[step] as unknown as UploadExceptionsWizardContextSteps);
  };

  const goToPreviousPage = () => {
    switch (wizardContext.page) {
      case UploadExceptionsWizardContextSteps.Review:
        wizardContext.setPage(UploadExceptionsWizardContextSteps.Upload);
        wizardContext.setShowChangeWarning(true);
        break;
    }
  };

  const validateAndAdvance = () => {
    let newPage: UploadExceptionsWizardContextSteps;
    switch (wizardContext.page) {
      case UploadExceptionsWizardContextSteps.Upload:
        newPage = UploadExceptionsWizardContextSteps.Review;
        break;
    }
    if (newPage) {
      wizardContext.setPage(newPage);
      wizardContext.setShowChangeWarning(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitClicked(true);
      const model: SpecialtyGroupException[] = wizardContext.validRecords;

      const validationMessages = await exceptionsService.createSpecialtyGroupExceptionsFromList(model);

      toast.success(`Exceptions have been successfully submitted.${validationMessages.length > 0 ? ` ${validationMessages.join(" ")}` : ""}`, {
        autoClose: 3000,
        containerId: "z-index-9999",
        onClose: () => {
          setRedirectToView(true);
        },
      });
    } catch (error) {
      setSubmitClicked(false);
      toast.error("There was an error processing your request. Please try again.", {
        containerId: "z-index-9999"
      });
    }
  }

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case UploadExceptionsWizardContextSteps.Upload:
        return <UploadExceptionsImport pageIsValid={setPageIsValid} />;
      case UploadExceptionsWizardContextSteps.Review:
        return <UploadExceptionsReview pageIsValid={setPageIsValid} />;
    }
  }, [wizardContext.page]);

  useEffect(() => {
    if (!wizardContext.loading) {
      focusFirstInteractiveElement(wizardBodyRef);
    }
  }, [wizardContext.loading]);

  if (redirectToView) {
    return <Redirect to="/exceptions/view" push={false} />;
  }

  return (
    <Page documentTitle="New Exceptions">
      <PageInner className="upload-exceptions-wizard">
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">New Exceptions</h1>
        </header>
        <WizardSteps currentStep={getCurrentStep()} steps={Object.values(humanReadablePageNames)} setStep={handleUpdatePage} />
        <div ref={wizardBodyRef}>
          {wizardContext.loading ?
            !!wizardContext.error ?
              null :
              <LoadingSpinner /> :
            currentWizardStep}
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <Link to="/exceptions/view" className="qes-button qes-button-secondary danger" data-testid="upload-exceptions-wizard__cancel">Cancel</Link>
        )}

        rightSide={(
          <div className="upload-exceptions-wizard__nav-buttons">
            {wizardContext.page !== UploadExceptionsWizardContextSteps.Upload &&
              <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="upload-exceptions-wizard__previous">Previous</QESButton>}
            {wizardContext.page === UploadExceptionsWizardContextSteps.Review ? (
              <QESButton
                disabled={!pageIsValid || submitClicked}
                qesType="primary"
                qesTestId="upload-exceptions-wizard__submit"
                className="ms-3"
                onClick={handleSubmit}>
                Submit
              </QESButton>
            ) : (
              <QESButton
                onClick={validateAndAdvance}
                disabled={!pageIsValid}
                qesTestId="upload-exceptions-wizard__next"
                className="ms-3">
                Next
              </QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
};

export default UploadExceptionsWizard;